import type { PriceBucket } from './types';

/** Keep sale-window dates in the exact YYYY-MM-DD format the ArcGIS query layer expects. */
export function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Shift a calendar date by whole months so sale-window presets stay month-aligned. */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

/** Format missing or sparse numeric data consistently with the UI's `N/A` fallback. */
export function formatCurrency(value: number | null, fractionDigits = 0): string {
  if (value === null || Number.isNaN(value)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Format generic counts and measurements while preserving a shared `N/A` placeholder. */
export function formatNumber(value: number | null): string {
  if (value === null || Number.isNaN(value)) return 'N/A';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

/** Price-per-square-foot is derived client-side, so it needs the same missing-data fallback as other fields. */
export function formatPricePerSqft(value: number | null): string {
  if (value === null || Number.isNaN(value)) return 'N/A';
  return `${formatCurrency(value, 2)} / sq ft`;
}

/** Keep compact sale prices short enough for legend chips and bucket labels. */
export function formatCompactCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `$${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value / 1_000_000)}M`;
  }

  if (value >= 1_000) {
    return `$${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value / 1_000)}k`;
  }

  return formatCurrency(value);
}

/** Price buckets are shown in tight UI spaces, so compact labels are the default. */
export function bucketRangeLabel(bucket: PriceBucket, compact = true): string {
  const start = compact ? formatCompactCurrency(bucket.start) : formatCurrency(bucket.start);
  const end = compact ? formatCompactCurrency(bucket.end) : formatCurrency(bucket.end);
  return `${start}-${end}`;
}

/** Escape remote parcel text before it is inserted into popup HTML. */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return entities[character];
  });
}
